import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { contactApi } from '@/services/api';
import { 
  ContactSubmission, 
  ContactSubmissionListParams, 
  ContactStatistics,
  UpdateContactSubmissionRequest 
} from '@/types';

// 状态标签组件
const StatusBadge: React.FC<{ status: ContactSubmission['status'] }> = ({ status }) => {
  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'pending':
        return { label: '待联系', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' };
      case 'contacted':
        return { label: '已联系', color: 'bg-blue-100 text-blue-800 border-blue-200' };
      case 'completed':
        return { label: '已完成', color: 'bg-green-100 text-green-800 border-green-200' };
      case 'invalid':
        return { label: '无效', color: 'bg-red-100 text-red-800 border-red-200' };
      default:
        return { label: '未知', color: 'bg-gray-100 text-gray-800 border-gray-200' };
    }
  };

  const config = getStatusConfig(status);
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${config.color}`}>
      {config.label}
    </span>
  );
};

// 优先级标签组件
const PriorityBadge: React.FC<{ priority: ContactSubmission['priority'] }> = ({ priority }) => {
  const getPriorityConfig = (priority: string) => {
    switch (priority) {
      case 'high':
        return { label: '高', color: 'bg-red-100 text-red-800' };
      case 'normal':
        return { label: '普通', color: 'bg-gray-100 text-gray-800' };
      case 'low':
        return { label: '低', color: 'bg-green-100 text-green-800' };
      default:
        return { label: '未知', color: 'bg-gray-100 text-gray-800' };
    }
  };

  const config = getPriorityConfig(priority);
  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
      {config.label}
    </span>
  );
};

// 联系详情模态框
const ContactDetailModal: React.FC<{
  contact: ContactSubmission | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (id: number, data: UpdateContactSubmissionRequest) => void;
}> = ({ contact, isOpen, onClose, onUpdate }) => {
  const [formData, setFormData] = useState<UpdateContactSubmissionRequest>({});
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (contact) {
      setFormData({
        status: contact.status,
        priority: contact.priority,
        notes: contact.notes || '',
        contacted_by: contact.contacted_by || '',
      });
    }
  }, [contact]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contact) return;

    setIsUpdating(true);
    try {
      await onUpdate(contact.id, formData);
      onClose();
    } catch (error) {
      console.error('更新失败:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  if (!isOpen || !contact) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-900">联系详情</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <span className="sr-only">关闭</span>
            ✕
          </button>
        </div>

        <div className="space-y-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">客户姓名</label>
              <p className="text-gray-900 font-semibold">{contact.name}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">联系电话</label>
              <p className="text-gray-900">{contact.phone}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">客户类型</label>
              <p className="text-gray-900">{contact.type === 'individual' ? '个人开发者' : '企业客户'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">意向套餐</label>
              <p className="text-gray-900">{contact.plan || '未选择'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">邮箱地址</label>
              <p className="text-gray-900">{contact.email || '未提供'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">提交时间</label>
              <p className="text-gray-900">{new Date(contact.created_at).toLocaleString('zh-CN')}</p>
            </div>
          </div>

          {contact.scenario && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">使用场景</label>
              <p className="text-gray-900 bg-gray-50 p-3 rounded-lg">{contact.scenario}</p>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">状态</label>
              <select
                value={formData.status || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as any }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="pending">待联系</option>
                <option value="contacted">已联系</option>
                <option value="completed">已完成</option>
                <option value="invalid">无效</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">优先级</label>
              <select
                value={formData.priority || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value as any }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="high">高</option>
                <option value="normal">普通</option>
                <option value="low">低</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">联系人员</label>
            <input
              type="text"
              value={formData.contacted_by || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, contacted_by: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="请输入联系人员姓名"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">备注信息</label>
            <textarea
              rows={4}
              value={formData.notes || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="请输入备注信息"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              取消
            </Button>
            <Button type="submit" disabled={isUpdating}>
              {isUpdating ? '更新中...' : '更新'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// 主组件
export const ContactManagement: React.FC = () => {
  const [contacts, setContacts] = useState<ContactSubmission[]>([]);
  const [statistics, setStatistics] = useState<ContactStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState<ContactSubmission | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState<ContactSubmissionListParams>({
    page: 1,
    size: 10,
  });
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState<ContactSubmission | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // 加载联系列表
  const loadContacts = async (params: ContactSubmissionListParams) => {
    try {
      setLoading(true);
      const response = await contactApi.getContactSubmissions(params);
      setContacts(response.items);
      setTotalPages(Math.ceil(response.total / (params.size || 10)));
    } catch (error) {
      console.error('加载联系列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 加载统计信息
  const loadStatistics = async () => {
    try {
      const stats = await contactApi.getContactStatistics();
      setStatistics(stats);
    } catch (error) {
      console.error('加载统计信息失败:', error);
    }
  };

  // 更新联系状态
  const handleUpdateContact = async (id: number, data: UpdateContactSubmissionRequest) => {
    try {
      await contactApi.updateContactSubmission(id, data);
      // 重新加载数据
      await loadContacts(filters);
      await loadStatistics();
    } catch (error) {
      console.error('更新联系状态失败:', error);
    }
  };

  // 查看详情
  const handleViewDetail = (contact: ContactSubmission) => {
    setSelectedContact(contact);
    setIsDetailModalOpen(true);
  };

  // 打开删除确认对话框
  const handleDeleteClick = (contact: ContactSubmission) => {
    setContactToDelete(contact);
    setDeleteModalOpen(true);
  };

  // 确认删除联系记录
  const handleConfirmDelete = async () => {
    if (!contactToDelete) return;

    setIsDeleting(true);
    try {
      await contactApi.deleteContactSubmission(contactToDelete.id);
      // 删除成功后重新加载列表
      await loadContacts(filters);
      await loadStatistics();
      setDeleteModalOpen(false);
      setContactToDelete(null);
    } catch (error) {
      console.error('删除联系记录失败:', error);
      // 可以在这里添加错误提示
    } finally {
      setIsDeleting(false);
    }
  };

  // 取消删除
  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setContactToDelete(null);
  };

  // 初始化加载
  useEffect(() => {
    loadContacts(filters);
    loadStatistics();
  }, []);

  // 处理分页
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    const newFilters = { ...filters, page };
    setFilters(newFilters);
    loadContacts(newFilters);
  };

  // 处理筛选
  const handleFilterChange = (key: keyof ContactSubmissionListParams, value: any) => {
    const newFilters = { ...filters, [key]: value, page: 1 };
    setFilters(newFilters);
    setCurrentPage(1);
    loadContacts(newFilters);
  };

  return (
    <div className="space-y-6">
      {/* 统计卡片 */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <div className="p-4">
              <div className="flex items-center">
                <div className="text-2xl">📋</div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">总咨询数</p>
                  <p className="text-2xl font-bold text-gray-900">{statistics.total}</p>
                </div>
              </div>
            </div>
          </Card>
          <Card>
            <div className="p-4">
              <div className="flex items-center">
                <div className="text-2xl">📅</div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">今日新增</p>
                  <p className="text-2xl font-bold text-blue-600">{statistics.today}</p>
                </div>
              </div>
            </div>
          </Card>
          <Card>
            <div className="p-4">
              <div className="flex items-center">
                <div className="text-2xl">⏰</div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">待处理</p>
                  <p className="text-2xl font-bold text-yellow-600">{statistics.status.pending}</p>
                </div>
              </div>
            </div>
          </Card>
          <Card>
            <div className="p-4">
              <div className="flex items-center">
                <div className="text-2xl">✅</div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">已完成</p>
                  <p className="text-2xl font-bold text-green-600">{statistics.status.completed}</p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* 筛选器 */}
      <Card>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">状态</label>
              <select
                value={filters.status || ''}
                onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">全部状态</option>
                <option value="pending">待联系</option>
                <option value="contacted">已联系</option>
                <option value="completed">已完成</option>
                <option value="invalid">无效</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">客户类型</label>
              <select
                value={filters.type || ''}
                onChange={(e) => handleFilterChange('type', e.target.value || undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">全部类型</option>
                <option value="individual">个人开发者</option>
                <option value="enterprise">企业客户</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">优先级</label>
              <select
                value={filters.priority || ''}
                onChange={(e) => handleFilterChange('priority', e.target.value || undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">全部优先级</option>
                <option value="high">高</option>
                <option value="normal">普通</option>
                <option value="low">低</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">搜索</label>
              <input
                type="text"
                placeholder="姓名、电话或套餐"
                value={filters.search || ''}
                onChange={(e) => handleFilterChange('search', e.target.value || undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
        </div>
      </Card>

      {/* 联系列表 */}
      <Card>
        <div className="p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">联系记录</h3>
          {loading ? (
            <div className="text-center py-8">
              <div className="text-gray-600">加载中...</div>
            </div>
          ) : contacts.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-600">暂无联系记录</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      客户信息
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      意向套餐
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      状态
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      优先级
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      提交时间
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {contacts.map((contact) => (
                    <tr key={contact.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col">
                          <div className="text-sm font-medium text-gray-900">{contact.name}</div>
                          <div className="text-sm text-gray-500">{contact.phone}</div>
                          <div className="text-xs text-gray-400">
                            {contact.type === 'individual' ? '个人开发者' : '企业客户'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {contact.plan || '未选择'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge status={contact.status} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <PriorityBadge priority={contact.priority} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(contact.created_at).toLocaleDateString('zh-CN')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewDetail(contact)}
                          >
                            查看详情
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteClick(contact)}
                            className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                          >
                            删除
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* 分页 */}
          {totalPages > 1 && (
            <div className="flex justify-between items-center mt-4">
              <div className="text-sm text-gray-700">
                第 {currentPage} 页，共 {totalPages} 页
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  上一页
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  下一页
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* 详情模态框 */}
      <ContactDetailModal
        contact={selectedContact}
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        onUpdate={handleUpdateContact}
      />

      {/* 删除确认对话框 */}
      {deleteModalOpen && contactToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
              <svg 
                className="w-6 h-6 text-red-600" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" 
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-center text-gray-900 mb-2">
              确认删除
            </h3>
            <p className="text-sm text-gray-500 text-center mb-6">
              您确定要删除客户 <span className="font-medium text-gray-900">{contactToDelete.name}</span> 的联系记录吗？
              <br />
              此操作无法撤销。
            </p>
            <div className="flex space-x-4">
              <Button
                variant="outline"
                className="flex-1"
                onClick={handleCancelDelete}
                disabled={isDeleting}
              >
                取消
              </Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? '删除中...' : '确认删除'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};